import React, { useCallback, useState } from "react";
import { loadStripe } from '@stripe/stripe-js';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API } from '../api';
import FullScreenLayout from '../components/FullScreenLayout';
import { Navigate, useLocation } from 'react-router-dom';
import { CheckoutProvider } from '@stripe/react-stripe-js';
import CheckoutForm from '../components/CheckoutForm';
import { BillingInformationState } from './BillingInformationState';
import { getUserState, useSelector } from '../store';
import { IampLogger } from "../utils/IampLogger";
import CheckoutSkeleton from "../components/CheckoutSkeleton";

export const Checkout = () => {
  // Load the Stripe promise
  const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_API_KEY, {
    betas: ['custom_checkout_beta_6'],
    locale: 'en'
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const location = useLocation();
  const { tier, subscriptionFrequency, priceIdList, isEuroCurrency, numberOfCreditsForSubscription, offPackageCreditCost, planName } =
    (location.state as BillingInformationState) || {};
  const { user } = useSelector(getUserState);

  // Redirect if any required billing information is missing
  if (!tier || !priceIdList || isEuroCurrency === undefined) {
    return <Navigate to="/tierselection" />;
  }


  const fetchClientSecret = useCallback(async () => {
    setIsLoading(true);
    // Create a Checkout Session
    return API(await fetchAuthSession()).post("/stripe/create-checkout-session", {
      priceIdList : priceIdList.split(','),
      tier,
      subscriptionFrequency,
      isEuroCurrency,
      user_email:user?.user_email
    })
      .then((res) => {
        return res.data.clientSecret;
      })
      .catch((error) => {
        setIsLoading(false);
        IampLogger.error('Error:', error.response.data.message);
        setErrorMessage("Error while creating checkout session: " + error.response.data.message)
        return
      });
  }, []);


  const appearance = {
    theme: 'flat',
    variables: {
      fontFamily: ' "Barlow", sans-serif',
      fontLineHeight: '1.5',
      borderRadius: '10px',
      colorBackground: '#F6F8FA',
      accessibleColorOnColorPrimary: '#262626'
    },
    rules: {
      '.Block': {
        backgroundColor: 'var(--colorBackground)',
        boxShadow: 'none',
        padding: '12px'
      },
      '.Input': {
        padding: '12px'
      },
      '.Input:disabled, .Input--invalid:disabled': {
        color: 'lightgray'
      },
      '.Tab': {
        padding: '10px 12px 8px 12px',
        border: 'none'
      },
      '.Tab:hover': {
        border: 'none',
        boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)'
      },
      '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
        border: 'none',
        backgroundColor: '#fff',
        boxShadow: '0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)'
      },
      '.Label': {
        fontWeight: '500'
      }
    }
  };

  const options = {fetchClientSecret , elementsOptions: {
    appearance,
  }};
  
  return (
    <FullScreenLayout
      title="Checkout"
      subtitle=""
    >
      <div id="checkout">
        <CheckoutProvider
          stripe={stripePromise}
          options={options}
        >
          <CheckoutForm tier={tier} subscriptionFrequency={subscriptionFrequency} planName={planName} offPackageCreditCost={offPackageCreditCost} numberOfCreditsForSubscription={numberOfCreditsForSubscription} setIsloading={setIsLoading}/>
        </CheckoutProvider>
        {isLoading && <CheckoutSkeleton/>}
      </div>
      {errorMessage && <div>{errorMessage}</div>}
    </FullScreenLayout>
  );
};
